import React from "react";
import { gql, useQuery } from "@apollo/client";
import { Instrument, InstrumentType } from "@syos-music/instruments";
import { Product } from "@syos-music/products";

import {
  styled,
  Container,
  Tabs as MuiTabs,
  Tab as MuiTab,
  List,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";

import Layout from "components/common/Layout";
import EntityTagsFormDialog from "components/entityTags/EntityTagsFormDialog";
import SignatureMouthpiecesListItem from "components/signatureMouthpiece/SignatureMouthpiecesListItem";

const { OriginalsType } = Product.displayModes.originalsMouthpiece;

const QUERY = gql`
  query ($filters: SignatureMouthpieces_FiltersInput!) {
    signatureMouthpieces(filters: $filters, sort: "weight") {
      edges {
        node {
          id

          ...SignatureMouthpiecesListItem_signatureMouthpiece
          ...EntityTagsFormDialog_signatureMouthpiece
        }
      }
    }
  }

  ${SignatureMouthpiecesListItem.fragments.signatureMouthpiece}
  ${EntityTagsFormDialog.fragments.signatureMouthpiece}
`;

const Tabs = styled(MuiTabs)(
  ({ theme }) => `
    margin: ${theme.spacing(-1, 0)};
    min-height: 56px;
  `
);

const Tab = styled(MuiTab)`
  min-height: 56px;
`;

const ORIGINALS = [];
for (const instrument of Instrument.instruments) {
  for (const instrumentType of InstrumentType.instrumentTypes(instrument)) {
    if (instrument === "saxophone" && instrumentType === "bass") continue;

    for (const originalsType of OriginalsType.originalsTypes) {
      ORIGINALS.push({ instrument, instrumentType, originalsType });
    }
  }
}

export default function SoundTagsPage() {
  const [type, setType] = React.useState("originals");
  const [entityMatch, setEntityMatch] = React.useState();

  const { data, loading } = useQuery(QUERY, {
    variables: {
      filters: {},
    },
    skip: type !== "signature",
  });

  return (
    <Container fixed>
      <Layout.Toolbar>
        <Tabs
          value={type}
          onChange={(evt, value) => setType(value)}
          variant="fullWidth"
        >
          <Tab value="originals" label="Originals" />
          <Tab value="signature" label="Signature" />
        </Tabs>
      </Layout.Toolbar>

      {loading && <Layout.Loading />}

      <EntityTagsFormDialog
        entityMatch={entityMatch}
        onClose={() => setEntityMatch(null)}
      />

      {type === "originals" && (
        <List>
          {ORIGINALS.map(({ instrument, instrumentType, originalsType }) => (
            <ListItem
              key={[instrument, instrumentType, originalsType].join("-")}
              button
              onClick={() =>
                setEntityMatch({
                  type: "originals",
                  instrument,
                  instrumentType,
                  originalsType,
                })
              }
              dense
              divider
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing="1ch">
                    <Instrument instrument={instrument} />
                    <InstrumentType
                      instrument={instrument}
                      instrumentType={instrumentType}
                    />
                    <OriginalsType originalsType={originalsType} />
                  </Stack>
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      {type === "signature" && (
        <List>
          {data?.signatureMouthpieces.edges.map(({ node }) => (
            <SignatureMouthpiecesListItem
              key={node.id}
              signatureMouthpiece={node}
              button
              onClick={() =>
                setEntityMatch({
                  type: "signature",
                  signatureMouthpiece: node,
                })
              }
            />
          ))}
        </List>
      )}
    </Container>
  );
}
